import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import type { Product } from '@/types';
import { api } from '@/client/lib/api';
import { useMeta } from '@/client/lib/useMeta';
import { ProductGrid } from '@/components/ProductGrid';
import { SectionHeader } from '@/components/SectionHeader';
import { Markdown } from '@/components/Markdown';
import { Breadcrumb } from '@/components/Breadcrumb';
import { LoadingState, EmptyState } from '@/components/States';
import { Newsletter } from '@/features/newsletter/Newsletter';
import { useI18n } from '@/client/i18n/I18nContext';
import { staticPageText } from '@/lib/seo';
import { FINDS_CATEGORY, FINDS_PATH, findsInDropOrder, findsOf } from '@/lib/catalog/finds';
import { lp } from '@/client/i18n/locale';

/**
 * /shop/hallazgos: Estilo's one-off pieces (lib/catalog/finds), newest drop
 * first and the sold ones last («Pieza vendida»), see findsInDropOrder. Same
 * text and order as the Worker's prerender (worker/lib/prerender findsHtml).
 * Then the «Hallazgos» signup (finds list only; the form needs JS, so the
 * prerender leaves it out, like /newsletter's).
 */
export function FindsPage() {
  const { t, locale } = useI18n();
  const [finds, setFinds] = useState<Product[] | null>(null);
  // Same <title>/description as the Worker (lib/seo STATIC_PAGES).
  useMeta({ ...staticPageText(FINDS_PATH, locale), path: FINDS_PATH });

  useEffect(() => {
    let alive = true;
    api
      .products({ category: FINDS_CATEGORY })
      .then((list) => {
        if (alive) setFinds(findsInDropOrder(findsOf(list)));
      })
      .catch(() => {
        if (alive) setFinds([]);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <div className="container">
      <Breadcrumb
        items={[
          { name: t('common.home'), path: '/' },
          { name: t('nav.shop'), path: '/shop' },
          { name: t(`shop.${FINDS_CATEGORY}.title`), path: `/shop/${FINDS_CATEGORY}` },
          { name: t('finds.title') },
        ]}
      />
      <SectionHeader as="h1" eyebrow={t('finds.eyebrow')} title={t('finds.title')} lede={t('finds.lede')} />

      {finds === null ? (
        <LoadingState />
      ) : finds.length === 0 ? (
        <EmptyState
          title={t('finds.emptyTitle')}
          message={t('finds.emptyMsg')}
          action={
            <Link to={lp('/newsletter')} className="link-underline">
              {t('finds.emptyCta')}
            </Link>
          }
        />
      ) : (
        <ProductGrid products={finds} compact />
      )}

      <div className="prose" style={{ maxWidth: 'var(--width-narrow)', marginTop: 'var(--space-8)', paddingBottom: 'var(--space-8)' }}>
        <Markdown source={t('finds.intro')} />
      </div>
      {/* The live drop alert first; La carta (monthly) only as a quieter link under it. */}
      <Newsletter
        lists={['finds']}
        defaultLists={['finds']}
        eyebrow={t('finds.letterEyebrow')}
        title={t('finds.letterTitle')}
        body={t('finds.letterBody')}
      />
      <p className="finds__letter-cta">
        <Link to={lp('/newsletter')} className="link-underline">
          {t('finds.newsletterCta')}
        </Link>
      </p>
    </div>
  );
}
